import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetRollCallHistoryQueryKey,
  useGetEvent,
  useGetRollCallHistory,
  type RollCallHistoryStudent,
  type RollCallHistoryStatus
} from '@cdf/api-client-react';
import { computeRollCallExceptions } from '@/lib/roll-call-exceptions';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  ArrowLeftIcon,
  CheckIcon,
  XIcon,
  MinusIcon,
  HistoryIcon,
  UserCheckIcon,
  LogOutIcon,
  AlertTriangleIcon
} from 'lucide-react';

/* ------------------------------ status cell ------------------------------ */

function StatusCell({ status, isException = false }: { status: RollCallHistoryStatus; isException?: boolean }) {
  const isPresent = status.status === 'present';
  const isAbsent = status.status === 'absent';

  const tooltipText = status.markedAt
    ? `${status.status} — ${status.markedBy ?? 'unknown'} at ${new Date(status.markedAt).toLocaleTimeString()}`
    : status.status === 'pending'
      ? 'Not yet marked'
      : `${status.status} — ${status.markedBy ?? 'unknown'}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={
              isException
                ? 'inline-flex cursor-default rounded-sm border-2 border-foreground px-1 py-0.5 font-bold'
                : 'inline-flex cursor-default'
            }
          >
            {isPresent && (
              <CheckIcon className="h-4 w-4 text-foreground" aria-label="Present" />
            )}
            {isAbsent && (
              <XIcon className="h-4 w-4 text-foreground" aria-label="Absent" />
            )}
            {!isPresent && !isAbsent && (
              <MinusIcon className="h-4 w-4 text-foreground/60" aria-label="Pending" />
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* --------------------------- sign-out column ----------------------------- */

function SignOutCell({ student }: { student: RollCallHistoryStudent }) {
  if (student.checkinStatus !== 'signed_out') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <UserCheckIcon className="h-3.5 w-3.5" />
        Checked in
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium">
      <LogOutIcon className="h-3.5 w-3.5" />
      {student.checkoutType
        ? student.checkoutType.replace(/_/g, ' ')
        : 'Signed out'}
      {student.signedOutBy && (
        <span className="text-muted-foreground">· {student.signedOutBy}</span>
      )}
    </span>
  );
}

/* ------------------------------ main page -------------------------------- */

export function EventHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: event } = useGetEvent(id!);
  const { data, isLoading } = useGetRollCallHistory(id!);

  // SSE: invalidate history query when entries change elsewhere
  React.useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/events/${id}/live`);
    es.onmessage = () => {
      queryClient.invalidateQueries({ queryKey: getGetRollCallHistoryQueryKey(id) });
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, [id, queryClient]);

  const rollCalls = data?.rollCalls ?? [];
  const students = data?.students ?? [];

  // Exception detection — pure, re-computes when history data changes (SSE-driven).
  const exceptions = React.useMemo(
    () => (data ? computeRollCallExceptions(data) : []),
    [data]
  );
  const exceptionMap = React.useMemo(() => {
    const map = new Map<string, Set<number>>();
    for (const exc of exceptions) {
      map.set(exc.studentId, new Set(exc.exceptionRollCallIndexes));
    }
    return map;
  }, [exceptions]);

  const [showExceptionsOnly, setShowExceptionsOnly] = React.useState(false);

  const visibleStudents = showExceptionsOnly
    ? students.filter((s) => exceptionMap.has(s.id))
    : students;

  return (
    <>
      <PageHeader
        title="Attendance history"
        description={event ? `${event.name} — ${new Date(event.date).toLocaleDateString()}` : 'Loading…'}
      />

      <div className="flex flex-wrap items-center gap-2 pb-4">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/events/${id}`}>
            <ArrowLeftIcon className="mr-1 h-4 w-4" />
            Back to event
          </Link>
        </Button>
        {students.length > 0 && (
          <Button
            variant={showExceptionsOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowExceptionsOnly((v) => !v)}
            className="ml-auto"
          >
            <AlertTriangleIcon className="mr-1.5 h-4 w-4" />
            Exceptions between roll calls
            {exceptions.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary-foreground px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary">
                {exceptions.length}
              </span>
            )}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card className="p-6">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="mt-3 h-8 w-full" />
          <Skeleton className="mt-3 h-8 w-full" />
          <Skeleton className="mt-3 h-8 w-3/4" />
        </Card>
      ) : students.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
          <HistoryIcon className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No students checked in for this event yet.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to={`/events/${id}`}>Go to event</Link>
          </Button>
        </Card>
      ) : showExceptionsOnly && exceptions.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
          <CheckIcon className="h-10 w-10 text-foreground" />
          <p className="text-sm font-medium">No exceptions</p>
          <p className="text-xs text-muted-foreground">
            Everyone accounted for — no student was present earlier and missing at a later roll call.
          </p>
          <Button variant="outline" size="sm" onClick={() => setShowExceptionsOnly(false)}>
            Show full history
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-10 bg-background min-w-[10rem]">
                    Student
                  </TableHead>
                  {rollCalls.map((rc) => (
                    <TableHead key={rc.id} className="text-center min-w-[5rem]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-xs font-medium">{rc.name}</span>
                        <span className="text-[10px] text-muted-foreground">#{rc.sortOrder + 1}</span>
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="min-w-[10rem]">Sign-out</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleStudents.map((student) => {
                  const excSet = exceptionMap.get(student.id);
                  return (
                    <TableRow key={student.id}>
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {student.firstName} {student.lastInitial}
                          </span>
                          <span className="text-xs text-muted-foreground">{student.group}</span>
                        </div>
                      </TableCell>
                      {student.statuses.map((status, i) => (
                        <TableCell key={i} className="text-center">
                          <StatusCell status={status} isException={excSet?.has(i) ?? false} />
                        </TableCell>
                      ))}
                      <TableCell>
                        <SignOutCell student={student} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Summary footer */}
          <div className="flex items-center gap-4 border-t px-4 py-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <CheckIcon className="h-3.5 w-3.5 text-foreground" /> Present
            </span>
            <span className="inline-flex items-center gap-1">
              <XIcon className="h-3.5 w-3.5" /> Absent
            </span>
            <span className="inline-flex items-center gap-1">
              <MinusIcon className="h-3.5 w-3.5 text-muted-foreground/50" /> Pending
            </span>
            <span className="ml-auto">
              {visibleStudents.length} {visibleStudents.length === 1 ? 'student' : 'students'} · {rollCalls.length}{' '}
              {rollCalls.length === 1 ? 'roll call' : 'roll calls'}
            </span>
          </div>
        </Card>
      )}
    </>
  );
}
